/**
 * CountersignPSA — public Jesse-countersign page reached via the token
 * link emailed by utils/bcPsaJesseCountersign.js. No auth — the token
 * in the URL IS the authorization. Mirrors the layout style of
 * pages/OfferReview.jsx so the listing-agent + countersign pages feel
 * consistent.
 *
 * States: loading → review (View PDF + Proceed) → sign (canvas + Submit) →
 *         done   → error
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export default function CountersignPSA() {
  // Capture the token ONCE, then strip it from the address bar (below) so it
  // never lingers in browser history or leaks via Referer. Refresh afterward has
  // no token → a "reopen the link" message, not a crash.
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token'));
  const [state, setState] = useState('loading');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [initialsHasStrokes, setInitialsHasStrokes] = useState(false);
  const canvasRef = useRef(null);
  const initialsRef = useRef(null);

  // ── Load token context ──────────────────────────────────────────────
  // Strip the token from the address bar on mount (history + Referer hygiene).
  useEffect(() => {
    if (window.location.search) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!token) { setError('Please reopen the link from your email.'); setState('error'); return; }
    (async () => {
      try {
        const r = await fetch(`${API}/api/countersign-psa/context`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) {
          setError(d.error || 'This link is invalid or has expired.');
          setState('error');
          return;
        }
        setData(d);
        setState('review');
      } catch (_) {
        setError('This link is invalid or has expired.');
        setState('error');
      }
    })();
  }, [token]);

  // ── Open the borrower-signed PDF in a new tab ───────────────────────
  const openPdf = () => {
    if (!data?.pdfBase64) return;
    const byteChars = atob(data.pdfBase64);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  // ── Signature canvas wiring ─────────────────────────────────────────
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // Match the canvas backing size to its CSS size at the device-pixel
    // ratio so the stamped PNG is crisp at any zoom level.
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width  = Math.round(rect.width  * dpr);
    canvas.height = Math.round(rect.height * dpr);
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    let drawing = false;
    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches?.[0];
      const cx = (t ? t.clientX : e.clientX) - r.left;
      const cy = (t ? t.clientY : e.clientY) - r.top;
      return { x: cx, y: cy };
    };
    const start = (e) => { e.preventDefault(); drawing = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move  = (e) => { if (!drawing) return; e.preventDefault(); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasStrokes(true); };
    const end   = () => { drawing = false; };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove',  move,  { passive: false });
    canvas.addEventListener('touchend',   end);

    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, []);

  useEffect(() => {
    if (state !== 'sign') return;
    const cleanup = initCanvas();
    return cleanup;
  }, [state, initCanvas]);

  // Initials canvas — same wiring as initCanvas but bound to its own
  // ref + state. Stamped on every page footer's "Seller Initials" box
  // by routes/countersignPsa.js via layout.initialBox.doorly.
  const initInitialsCanvas = useCallback(() => {
    const canvas = initialsRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width  = Math.round(rect.width  * dpr);
    canvas.height = Math.round(rect.height * dpr);
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    let drawing = false;
    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches?.[0];
      return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top };
    };
    const start = (e) => { e.preventDefault(); drawing = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move  = (e) => { if (!drawing) return; e.preventDefault(); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setInitialsHasStrokes(true); };
    const end   = () => { drawing = false; };
    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove',  move,  { passive: false });
    canvas.addEventListener('touchend',   end);
    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, []);

  useEffect(() => {
    if (state !== 'sign') return;
    const cleanup = initInitialsCanvas();
    return cleanup;
  }, [state, initInitialsCanvas]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStrokes(false);
    initCanvas();
  };

  const clearInitials = () => {
    const canvas = initialsRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setInitialsHasStrokes(false);
    initInitialsCanvas();
  };

  const handleSubmit = async () => {
    if (!hasStrokes || !initialsHasStrokes || submitting) return;
    setSubmitting(true);
    try {
      const signatureDataUrl = canvasRef.current.toDataURL('image/png');
      const initialsDataUrl  = initialsRef.current.toDataURL('image/png');
      const r = await fetch(`${API}/api/countersign-psa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, signatureDataUrl, initialsDataUrl }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(d.error || 'Submission failed. Please try again.');
        setState('error');
        return;
      }
      setState('done');
    } catch (_) {
      setError('Something went wrong. Please try again.');
      setState('error');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render states ────────────────────────────────────────────────────
  if (state === 'loading') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: '#9ca3af' }}>
        <div style={{ width: 28, height: 28, border: '3px solid #e5e7eb', borderTopColor: '#3a6ad9', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 20 }}>
        <div style={{ maxWidth: 480, textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
          <div style={{ color: '#ff082f', fontSize: 16, fontWeight: 700, marginBottom: 8 }}>This link is no longer valid.</div>
          <div style={{ color: '#6b7280', fontSize: 14, marginBottom: 12 }}>{error || 'Contact support@godoorly.com for assistance.'}</div>
        </div>
      </div>
    );
  }

  if (state === 'done') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 20 }}>
        <div style={{ maxWidth: 520, textAlign: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 14 }}>✓</div>
          <div style={{ color: '#166534', fontSize: 20, fontWeight: 800, marginBottom: 10 }}>Contract fully executed.</div>
          <div style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.6 }}>
            The borrower has been notified and the fully executed PDF is saved to the loan file.
          </div>
        </div>
      </div>
    );
  }

  // review + sign share the page chrome
  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f9', padding: '40px 20px' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.08)' }}>

        {/* Header */}
        <div style={{ background: '#3a6ad9', padding: '28px 36px', textAlign: 'center' }}>
          <div style={{ fontSize: 26, fontWeight: 900, color: '#fffd5b', letterSpacing: -0.3 }}>Doorly</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Borrower PSA Countersign</div>
        </div>

        {/* Context */}
        <div style={{ padding: '28px 36px 8px' }}>
          <p style={{ margin: '0 0 6px', fontSize: 13, color: '#6b7280' }}>Hi Jesse,</p>
          <h1 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800, color: '#111827', lineHeight: 1.3 }}>
            {data?.borrowerName || 'Borrower'}
          </h1>
          <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
            {data?.propertyAddress || ''}
          </p>
        </div>

        {state === 'review' && (
          <>
            <div style={{ padding: '20px 36px', borderBottom: '1px solid #f3f4f6' }}>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                The borrower has completed signing the Purchase and Sale Agreement. Review the signed PDF, then proceed
                to apply your countersignature to fully execute the contract.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <button onClick={openPdf}
                  style={{ padding: '12px 24px', background: '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: 10, fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
                  View Signed PDF
                </button>
                <button onClick={() => setState('sign')}
                  style={{ padding: '12px 24px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
                  Proceed to Sign →
                </button>
              </div>
            </div>
            <div style={{ padding: '18px 36px 28px', fontSize: 12, color: '#9ca3af' }}>
              This link is single-use and expires in 7 days from when it was sent.
            </div>
          </>
        )}

        {state === 'sign' && (
          <div style={{ padding: '20px 36px 28px' }}>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
              Draw your signature and initials below. Click <strong>Submit</strong> to fully execute the contract on behalf of
              Go Doorly, LLC.
            </p>

            <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Signature
            </div>
            <div style={{ position: 'relative', border: '1px solid #d1d5db', borderRadius: 10, background: '#fff', overflow: 'hidden' }}>
              <canvas
                ref={canvasRef}
                style={{ display: 'block', width: '100%', height: 180, touchAction: 'none', cursor: 'crosshair' }}
              />
              {!hasStrokes && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', color: '#d1d5db', fontStyle: 'italic', fontSize: 13 }}>
                  Sign here
                </div>
              )}
            </div>
            <button onClick={clearCanvas}
              style={{ marginTop: 8, padding: '6px 14px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Clear signature
            </button>

            <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', margin: '18px 0 6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Initials
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6 }}>
              Stamped on every page's "Seller Initials" box throughout the contract.
            </div>
            <div style={{ position: 'relative', border: '1px solid #d1d5db', borderRadius: 10, background: '#fff', overflow: 'hidden', width: 220 }}>
              <canvas
                ref={initialsRef}
                style={{ display: 'block', width: '100%', height: 100, touchAction: 'none', cursor: 'crosshair' }}
              />
              {!initialsHasStrokes && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', color: '#d1d5db', fontStyle: 'italic', fontSize: 12 }}>
                  Your initials
                </div>
              )}
            </div>
            <button onClick={clearInitials}
              style={{ marginTop: 8, padding: '6px 14px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Clear initials
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}>
              <button onClick={handleSubmit} disabled={!hasStrokes || !initialsHasStrokes || submitting}
                style={{
                  marginLeft: 'auto',
                  padding: '12px 28px',
                  background: hasStrokes && initialsHasStrokes && !submitting ? '#3a6ad9' : '#e5e7eb',
                  color:      hasStrokes && initialsHasStrokes && !submitting ? '#fff'    : '#9ca3af',
                  border: 'none', borderRadius: 10, fontWeight: 800, fontSize: 14,
                  cursor: hasStrokes && initialsHasStrokes && !submitting ? 'pointer' : 'not-allowed',
                }}>
                {submitting ? 'Submitting…' : 'Submit Signature'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
